import React, { useState } from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'

import { useConsent, ConsentState } from '../../lib/consent/ConsentContext'
import { mediaQueries } from '../Layout/Themes'

const Overlay = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    background: ${({ theme }) => theme.colors.backgrounds.footer};
    border-top: 2px solid ${({ theme }) => theme.colors.colors.Gold};
    padding: 1.5rem 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
`

const Container = styled.div`
    display: grid;
    grid-template-columns: 0% auto 0%;

    ${mediaQueries("Laptop")`
        grid-template-columns: 10% auto 10%;
    `};

    ${mediaQueries("Desktop")`
        grid-template-columns: 22% auto 22%;
    `};
`

const InnerContent = styled.div`
    grid-column: 2;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0 2rem;

    ${mediaQueries("Laptop")`
        padding: 0;
    `};
`

const TextSection = styled.div`
    flex: 1;
`

const Title = styled.p`
    font-family: ${({ theme }) => theme.font.headings.family};
    font-weight: ${({ theme }) => theme.font.headings.weight.medium};
    font-size: 1.2rem;
    text-transform: uppercase;
    margin: 0 0 0.25rem 0;
    color: ${({ theme }) => theme.colors.colors.White};
`

const Description = styled.p`
    font-weight: ${({ theme }) => theme.font.body.weight.medium};
    font-size: 0.85rem;
    margin: 0;
    color: ${({ theme }) => theme.colors.colors.White};
    line-height: 1.4;

    a {
        color: ${({ theme }) => theme.colors.colors.Gold};
        text-decoration: underline;
    }
`

const ButtonGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    ${mediaQueries("Tablet")`
        flex-direction: row;
        gap: 0.75rem;
        justify-content: flex-start;
    `};
`

const Button = styled.button`
    border-radius: 8px;
    font-size: 0.9rem;
    padding: 0.5rem 1.5rem;
    text-transform: uppercase;
    font-family: ${({ theme }) => theme.font.headings.family};
    font-weight: ${({ theme }) => theme.font.headings.weight.medium};
    cursor: pointer;
    transition: all 0.3s linear;
    white-space: nowrap;
`

const AcceptButton = styled(Button)`
    background: ${({ theme }) => theme.colors.colors.Gold};
    color: #000;
    border: 1px solid ${({ theme }) => theme.colors.colors.Gold};

    &:hover {
        opacity: 0.85;
    }
`

const RejectButton = styled(Button)`
    background: transparent;
    color: ${({ theme }) => theme.colors.colors.White};
    border: 1px solid ${({ theme }) => theme.colors.colors.White};

    &:hover {
        opacity: 0.7;
    }
`

const SettingsButton = styled(Button)`
    background: transparent;
    color: ${({ theme }) => theme.colors.colors.Gold};
    border: 1px solid ${({ theme }) => theme.colors.colors.Gold};

    &:hover {
        background: ${({ theme }) => theme.colors.colors.Gold};
        color: #000;
    }
`

const CategorySection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.5rem;
    padding-top: 0.75rem;
    border-top: 1px solid ${({ theme }) => theme.colors.colors.Gold}33;
`

const CategoryRow = styled.label`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-weight: ${({ theme }) => theme.font.body.weight.medium};
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.colors.White};
    cursor: pointer;
`

const CategoryLabel = styled.span`
    flex: 1;
`

const CategoryHint = styled.span`
    font-size: 0.75rem;
    opacity: 0.6;
`

const Toggle = styled.input`
    width: 40px;
    height: 22px;
    appearance: none;
    background: ${({ theme }) => theme.colors.colors.Grey3};
    border-radius: 11px;
    position: relative;
    cursor: pointer;
    transition: background 0.2s;
    flex-shrink: 0;

    &:checked {
        background: ${({ theme }) => theme.colors.colors.Gold};
    }

    &::after {
        content: '';
        position: absolute;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: white;
        top: 2px;
        left: 2px;
        transition: transform 0.2s;
    }

    &:checked::after {
        transform: translateX(18px);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

const ConsentBanner: React.FC = () => {
    const { showBanner, acceptAll, rejectAll, saveConsent } = useConsent()
    const [marketing, setMarketing] = useState(false)

    if (!showBanner) return null

    const handleSaveSettings = () => {
        saveConsent({ marketing })
    }

    return (
        <Overlay>
            <Container>
                <InnerContent>
                <TextSection>
                    <Title>Cookie-Einstellungen</Title>
                    <Description>
                        Wir nutzen Cookies für Marketingzwecke (Facebook Pixel).
                        Notwendige Funktionen sind immer aktiv.
                        Mehr Infos in unserer{' '}
                        <Link to="/datenschutz">Datenschutzerklärung</Link>.
                    </Description>

                    <CategorySection>
                        <CategoryRow>
                            <CategoryLabel>
                                Notwendig
                                <CategoryHint> — Immer aktiv</CategoryHint>
                            </CategoryLabel>
                            <Toggle type="checkbox" checked disabled />
                        </CategoryRow>
                        <CategoryRow>
                            <CategoryLabel>
                                Marketing
                                <CategoryHint> — Facebook Pixel für Anzeigen & Analysen</CategoryHint>
                            </CategoryLabel>
                            <Toggle
                                type="checkbox"
                                checked={marketing}
                                onChange={(e) => setMarketing(e.target.checked)}
                            />
                        </CategoryRow>
                    </CategorySection>
                </TextSection>

                <ButtonGroup>
                    <AcceptButton onClick={acceptAll}>
                        Alle akzeptieren
                    </AcceptButton>
                    <RejectButton onClick={rejectAll}>
                        Alle ablehnen
                    </RejectButton>
                    <SettingsButton onClick={handleSaveSettings}>
                        Auswahl speichern
                    </SettingsButton>
                </ButtonGroup>
                </InnerContent>
            </Container>
        </Overlay>
    )
}

export default ConsentBanner
